import React, { useState } from 'react';
import { 
  ClipboardList, 
  User,
  Calendar,
  CheckCircle,
  Clock,
  AlertCircle,
  Plus,
  Filter,
  Eye
} from 'lucide-react';

interface WorkOrder {
  id: string;
  type: 'install' | 'maintenance' | 'inspection';
  title: string;
  description: string;
  status: 'open' | 'in_progress' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high';
  assignee_name?: string;
  created_by: string;
  due_date?: string;
  created_at: string;
  completed_at?: string;
  location?: string;
  estimated_hours?: number;
  notes?: string;
}

const WorkOrders: React.FC = () => {
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState<WorkOrder | null>(null);

  const workOrders: WorkOrder[] = [
    {
      id: 'wo-001',
      type: 'maintenance',
      title: 'Valve Inspection Zone A-1',
      description: 'Monthly inspection and calibration of valve control system',
      status: 'open',
      priority: 'medium',
      assignee_name: 'Jean Dupont',
      created_by: 'Marie Laurent',
      due_date: '2024-01-20T10:00:00Z',
      created_at: '2024-01-15T08:30:00Z',
      location: 'Zone A-1, Domaine Les Vignes',
      estimated_hours: 2,
      notes: 'Check pressure readings and clean valve filters'
    },
    {
      id: 'wo-002',
      type: 'install',
      title: 'New Moisture Sensor Installation',
      description: 'Install additional moisture sensor in Zone B-3 for better coverage',
      status: 'in_progress',
      priority: 'high',
      assignee_name: 'Pierre Martin',
      created_by: 'Admin System',
      due_date: '2024-01-18T14:00:00Z',
      created_at: '2024-01-14T09:15:00Z',
      location: 'Zone B-3, Coopérative Hérault',
      estimated_hours: 4,
      notes: 'Sensor model: SoilSense-Pro, depth: 30cm'
    },
    {
      id: 'wo-003',
      type: 'inspection',
      title: 'Pump Station Safety Check',
      description: 'Quarterly safety inspection of main pump station',
      status: 'completed',
      priority: 'high',
      assignee_name: 'Marc Dubois',
      created_by: 'Safety Protocol',
      due_date: '2024-01-16T09:00:00Z',
      created_at: '2024-01-10T14:20:00Z',
      completed_at: '2024-01-16T11:30:00Z',
      location: 'Central Pump Station',
      estimated_hours: 3,
      notes: 'All safety systems functioning correctly. Pressure relief valve tested.'
    },
    {
      id: 'wo-004',
      type: 'maintenance',
      title: 'Gateway Firmware Update',
      description: 'Update firmware on gateway devices to version 2.1.5',
      status: 'open',
      priority: 'low',
      assignee_name: 'Sophie Chen',
      created_by: 'IT Department',
      due_date: '2024-01-25T16:00:00Z',
      created_at: '2024-01-15T10:45:00Z',
      location: 'All Gateway Locations',
      estimated_hours: 1,
      notes: 'Remote update possible for most devices'
    },
    {
      id: 'wo-005',
      type: 'maintenance',
      title: 'Leak Detection System Check',
      description: 'Investigate and repair potential leak in Zone A-2 irrigation line',
      status: 'open',
      priority: 'high',
      assignee_name: 'Jean Dupont',
      created_by: 'Alert System',
      due_date: '2024-01-17T08:00:00Z',
      created_at: '2024-01-15T14:30:00Z',
      location: 'Zone A-2, Field Section North',
      estimated_hours: 6,
      notes: 'Leak detected by automated system. Flow rate 40% above baseline.'
    },
    {
      id: 'wo-006',
      type: 'inspection',
      title: 'pH Sensor Calibration',
      description: 'Monthly calibration of pH sensors across all zones',
      status: 'cancelled',
      priority: 'medium',
      created_by: 'Quality Control',
      due_date: '2024-01-15T12:00:00Z',
      created_at: '2024-01-08T11:00:00Z',
      location: 'All Zones',
      estimated_hours: 4,
      notes: 'Cancelled - New automated calibration system installed'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'var(--color-info)';
      case 'in_progress': return 'var(--color-warning)';
      case 'completed': return 'var(--color-success)';
      case 'cancelled': return 'var(--color-danger)';
      default: return 'var(--text-secondary)';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'var(--color-danger)';
      case 'medium': return 'var(--color-warning)';
      case 'low': return 'var(--color-info)';
      default: return 'var(--text-secondary)';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'install': return '🔧';
      case 'maintenance': return '⚙️';
      case 'inspection': return '🔍';
      default: return '📋';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open': return Clock;
      case 'in_progress': return AlertCircle;
      case 'completed': return CheckCircle;
      case 'cancelled': return Clock;
      default: return Clock;
    }
  };

  const getTimeRemaining = (dueDate: string) => {
    const now = new Date();
    const due = new Date(dueDate);
    const diffMs = due.getTime() - now.getTime();
    
    if (diffMs <= 0) return 'Overdue';
    
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (diffDays > 0) return `${diffDays}d ${diffHours}h`;
    return `${diffHours}h`;
  };

  const filteredOrders = workOrders.filter(order => {
    const statusMatch = filterStatus === 'all' || order.status === filterStatus;
    const typeMatch = filterType === 'all' || order.type === filterType;
    const priorityMatch = filterPriority === 'all' || order.priority === filterPriority;
    return statusMatch && typeMatch && priorityMatch;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-heading-2 text-[var(--text-primary)]">Work Orders</h1>
          <p className="text-body text-[var(--text-secondary)] mt-1">
            Manage field tasks, maintenance, and inspections
          </p>
        </div>

        <button className="btn-primary flex items-center space-x-2" data-testid="create-work-order">
          <Plus className="w-4 h-4" />
          <span>New Work Order</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { 
            label: 'Total Orders', 
            value: workOrders.length, 
            color: 'var(--color-primary)',
            icon: ClipboardList
          },
          { 
            label: 'Open', 
            value: workOrders.filter(w => w.status === 'open').length, 
            color: 'var(--color-info)',
            icon: Clock
          },
          { 
            label: 'In Progress', 
            value: workOrders.filter(w => w.status === 'in_progress').length, 
            color: 'var(--color-warning)',
            icon: AlertCircle
          },
          { 
            label: 'Completed', 
            value: workOrders.filter(w => w.status === 'completed').length, 
            color: 'var(--color-success)',
            icon: CheckCircle
          }
        ].map((stat, index) => (
          <div key={index} className="card text-center">
            <stat.icon 
              className="w-6 h-6 mx-auto mb-2" 
              style={{ color: stat.color }} 
            />
            <div 
              className="text-2xl font-bold mb-1"
              style={{ color: stat.color }}
            >
              {stat.value}
            </div>
            <div className="text-sm text-[var(--text-secondary)]">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="flex items-center space-x-2">
          <Filter className="w-4 h-4 text-[var(--text-secondary)]" />
          <label className="text-sm font-medium text-[var(--text-primary)]">Status:</label>
          <select 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
            className="text-sm border border-[var(--border-light)] rounded-lg px-3 py-2 bg-[var(--surface)]"
            data-testid="work-order-status-filter"
          >
            <option value="all">All</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-[var(--text-primary)]">Type:</label>
          <select 
            value={filterType} 
            onChange={(e) => setFilterType(e.target.value)}
            className="text-sm border border-[var(--border-light)] rounded-lg px-3 py-2 bg-[var(--surface)]"
            data-testid="work-order-type-filter"
          >
            <option value="all">All Types</option>
            <option value="install">Installation</option>
            <option value="maintenance">Maintenance</option>
            <option value="inspection">Inspection</option>
          </select>
        </div>

        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-[var(--text-primary)]">Priority:</label>
          <select 
            value={filterPriority} 
            onChange={(e) => setFilterPriority(e.target.value)}
            className="text-sm border border-[var(--border-light)] rounded-lg px-3 py-2 bg-[var(--surface)]"
            data-testid="work-order-priority-filter"
          >
            <option value="all">All</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
      </div>

      {/* Work Orders List */}
      <div className="space-y-4">
        {filteredOrders.map((order) => {
          const StatusIcon = getStatusIcon(order.status);
          
          return (
            <div 
              key={order.id}
              className="card hover:shadow-lg transition-all cursor-pointer"
              onClick={() => setSelectedOrder(order)}
              data-testid={`work-order-card-${order.id}`}
            >
              <div className="flex items-start space-x-4">
                {/* Type & Priority Indicators */}
                <div className="flex flex-col items-center space-y-2">
                  <div className="text-2xl">{getTypeIcon(order.type)}</div>
                  <div 
                    className="w-2 h-8 rounded-full"
                    style={{ backgroundColor: getPriorityColor(order.priority) }}
                    title={`${order.priority} priority`}
                  />
                </div>

                {/* Order Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="text-heading-4 text-[var(--text-primary)] mb-1">
                        {order.title}
                      </h3>
                      <p className="text-body-sm text-[var(--text-secondary)]">
                        {order.description}
                      </p>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <StatusIcon 
                        className="w-5 h-5" 
                        style={{ color: getStatusColor(order.status) }}
                      />
                      <span 
                        className="px-2 py-1 text-xs font-medium rounded-full text-white capitalize"
                        style={{ backgroundColor: getStatusColor(order.status) }}
                      >
                        {order.status.replace('_', ' ')}
                      </span>
                    </div>
                  </div>

                  {/* Order Details Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                    {order.assignee_name && (
                      <div className="flex items-center space-x-2">
                        <User className="w-4 h-4 text-[var(--text-secondary)]" />
                        <div>
                          <div className="text-xs text-[var(--text-secondary)]">Assigned to</div>
                          <div className="text-sm font-medium text-[var(--text-primary)]">
                            {order.assignee_name}
                          </div>
                        </div>
                      </div>
                    )}

                    {order.due_date && (
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4 text-[var(--text-secondary)]" />
                        <div>
                          <div className="text-xs text-[var(--text-secondary)]">Due in</div>
                          <div className={`text-sm font-medium ${
                            getTimeRemaining(order.due_date) === 'Overdue' 
                              ? 'text-[var(--color-danger)]' 
                              : 'text-[var(--text-primary)]'
                          }`}>
                            {getTimeRemaining(order.due_date)}
                          </div>
                        </div>
                      </div>
                    )}

                    {order.location && (
                      <div>
                        <div className="text-xs text-[var(--text-secondary)]">Location</div>
                        <div className="text-sm text-[var(--text-primary)]">{order.location}</div>
                      </div>
                    )}

                    {order.estimated_hours && (
                      <div>
                        <div className="text-xs text-[var(--text-secondary)]">Est. Time</div>
                        <div className="text-sm text-[var(--text-primary)]">
                          {order.estimated_hours}h
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Tags */}
                  <div className="flex items-center space-x-2">
                    <span className="text-xs px-2 py-1 bg-[var(--gray-200)] text-[var(--text-secondary)] rounded-full capitalize">
                      {order.type}
                    </span>
                    <span 
                      className="text-xs px-2 py-1 rounded-full text-white capitalize"
                      style={{ backgroundColor: getPriorityColor(order.priority) }}
                    >
                      {order.priority} priority
                    </span>
                    <span className="text-xs text-[var(--text-tertiary)]">
                      Created by {order.created_by} • {new Date(order.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                {/* Action Button */}
                <button 
                  className="px-3 py-1 text-sm text-[var(--color-primary)] hover:text-[var(--color-primary-700)] font-medium"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedOrder(order);
                  }}
                  data-testid={`view-work-order-${order.id}`}
                >
                  <Eye className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredOrders.length === 0 && (
        <div className="text-center py-12">
          <ClipboardList className="w-12 h-12 text-[var(--text-secondary)] mx-auto mb-4" />
          <h3 className="text-heading-4 text-[var(--text-primary)] mb-2">
            No work orders found
          </h3>
          <p className="text-body text-[var(--text-secondary)]">
            Try adjusting your filters or create a new work order
          </p>
        </div>
      )}

      {/* Work Order Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-[var(--surface)] rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-start space-x-4">
                <div className="text-3xl">{getTypeIcon(selectedOrder.type)}</div>
                <div>
                  <h2 className="text-heading-3 text-[var(--text-primary)]">
                    {selectedOrder.title}
                  </h2>
                  <p className="text-body text-[var(--text-secondary)]">
                    {selectedOrder.description}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedOrder(null)}
                className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                data-testid="close-work-order-modal"
              >
                ✕
              </button>
            </div>

            <div className="space-y-6">
              {/* Status and Priority */}
              <div className="flex items-center space-x-4">
                <span 
                  className="px-3 py-1 text-sm font-medium rounded-full text-white capitalize"
                  style={{ backgroundColor: getStatusColor(selectedOrder.status) }}
                >
                  {selectedOrder.status.replace('_', ' ')}
                </span>
                <span 
                  className="px-3 py-1 text-sm font-medium rounded-full text-white capitalize"
                  style={{ backgroundColor: getPriorityColor(selectedOrder.priority) }}
                >
                  {selectedOrder.priority} Priority
                </span>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-[var(--text-primary)] mb-1">
                      Assignment
                    </h4>
                    <p className="text-sm text-[var(--text-secondary)]">
                      {selectedOrder.assignee_name ? (
                        <>Assigned to {selectedOrder.assignee_name}</>
                      ) : (
                        'Unassigned'
                      )}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-[var(--text-primary)] mb-1">
                      Location
                    </h4>
                    <p className="text-sm text-[var(--text-secondary)]">
                      {selectedOrder.location || 'Not specified'}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-[var(--text-primary)] mb-1">
                      Estimated Time
                    </h4>
                    <p className="text-sm text-[var(--text-secondary)]">
                      {selectedOrder.estimated_hours ? `${selectedOrder.estimated_hours} hours` : 'Not specified'}
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-[var(--text-primary)] mb-1">
                      Created
                    </h4>
                    <p className="text-sm text-[var(--text-secondary)]">
                      {new Date(selectedOrder.created_at).toLocaleString()} by {selectedOrder.created_by}
                    </p>
                  </div>

                  {selectedOrder.due_date && (
                    <div>
                      <h4 className="text-sm font-medium text-[var(--text-primary)] mb-1">
                        Due Date
                      </h4>
                      <p className={`text-sm ${
                        getTimeRemaining(selectedOrder.due_date) === 'Overdue'
                          ? 'text-[var(--color-danger)]'
                          : 'text-[var(--text-secondary)]'
                      }`}>
                        {new Date(selectedOrder.due_date).toLocaleString()}
                        <span className="ml-2">({getTimeRemaining(selectedOrder.due_date)})</span>
                      </p>
                    </div>
                  )}

                  {selectedOrder.completed_at && (
                    <div>
                      <h4 className="text-sm font-medium text-[var(--text-primary)] mb-1">
                        Completed
                      </h4>
                      <p className="text-sm text-[var(--color-success)]">
                        {new Date(selectedOrder.completed_at).toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Notes */}
              {selectedOrder.notes && (
                <div>
                  <h4 className="text-sm font-medium text-[var(--text-primary)] mb-2">
                    Notes
                  </h4>
                  <div className="p-4 bg-[var(--gray-50)] rounded-lg">
                    <p className="text-sm text-[var(--text-secondary)]">
                      {selectedOrder.notes}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkOrders;